import { api } from '../api.js';
import { subscribe } from '../state.js';
import { createGraphRenderer, GraphRenderer } from '../components/graphRenderer.js';
import { createGraphSettingsPanel, getGraphSettings } from '../components/graphSettings.js';
import { getTranslatedFields, translate } from '../lib/i18n.js';
import { createGraphSearch } from '../components/graphSearch.js';
import { extractNeighborhood } from '../lib/graphUtils.js';
import { createDepthSlider } from '../components/depthSlider.js';
import { showConceptInSidebar, showCrosslinkInSidebar } from '../components/graphSidebar.js';
import type { ApiGraphData, ApiGraphNode, ApiGraphEdge, ApiTagInfo } from '../../shared/types.js';

export async function renderGraphView(container: HTMLElement): Promise<void | (() => void)> {
  let time = Date.now();
  container.innerHTML = `<div class="loading">${translate('graph.loading', 'loading network...')}</div>`;

  let langUnsub: (() => void) | null = null;
  let currentRenderer: GraphRenderer | null = null;

  // Neighbor mode state (persists across language re-renders)
  let neighborMode = false;
  let neighborCenterSlug: string | null = null;
  let neighborDepth = 1;

  try {
    const graphData = await api.getGlobalGraph() as ApiGraphData;

    // Shared references set by renderContent, used by neighbor mode functions
    let graphContainer: HTMLElement | null = null;
    let backButton: HTMLButtonElement | null = null;
    let sidebar: HTMLElement | null = null;
    let depthSlider: ReturnType<typeof createDepthSlider> | null = null;
    let current: {
      renderer: GraphRenderer;
      settingsPanel: ReturnType<typeof createGraphSettingsPanel>;
      search: ReturnType<typeof createGraphSearch>;
      handleKeydown: (e: KeyboardEvent) => void;
    } | null = null;

    function closeSidebar(): void {
      if (sidebar) sidebar.style.display = 'none';
    }

    // --- Sidebar helpers (operate on the full graphData for lookups) ---

    function showCrosslinkSidebar(sourceSlug: string, targetSlug: string, crosslinkId?: number): void {
      const edge = crosslinkId
        ? graphData.edges.find((e: ApiGraphEdge) => e.id === crosslinkId)
        : graphData.edges.find((e: ApiGraphEdge) =>
            (e.source === sourceSlug && e.target === targetSlug) ||
            (e.source === targetSlug && e.target === sourceSlug)
          );

      if (!edge) {
        sidebar!.style.display = 'block';
        (sidebar!.querySelector('.graph-sidebar__content') as HTMLElement).innerHTML = `<p class="form-error">${translate('graph.crosslink_not_found', 'Crosslink not found.')}</p>`;
        return;
      }

      const rerender = (): void => { closeSidebar(); renderGraphView(container); };
      showCrosslinkInSidebar(sidebar!, edge, graphData.nodes, {
        onReverse: rerender,
        onDelete: rerender,
      });
    }

    function showConceptSidebar(slug: string): void {
      const node = graphData.nodes.find((n: ApiGraphNode) => n.slug === slug);
      if (!node) return;

      showConceptInSidebar(sidebar!, node, {
        onTagChange: (s: string, tags: ApiTagInfo[]) => {
          if (current?.renderer) current.renderer.updateNodeState(s, '', tags);
        },
        onDelete: () => renderGraphView(container),
      });
    }

    // --- Density stats updater ---

    function updateDensityStats(data: { nodes: readonly unknown[]; edges: readonly unknown[] }): void {
      const statsEl = graphContainer?.querySelector('.graph-density-stats') as HTMLElement | null;
      if (!statsEl) return;
      const nodeCount = data.nodes.length;
      const edgeCount = data.edges.length;
      const density = nodeCount > 0 ? (edgeCount / nodeCount).toFixed(2) : '0';
      statsEl.innerHTML = `
        <span class="graph-density-stats__item"><span class="graph-density-stats__value">${nodeCount}</span> ${translate('stats.nodes', 'nodes')}</span>
        <span class="graph-density-stats__item"><span class="graph-density-stats__value">${edgeCount}</span> ${translate('stats.edges', 'edges')}</span>
        <span class="graph-density-stats__item"><span class="graph-density-stats__value">${density}</span> ${translate('stats.density', 'density')}</span>
      `;
    }

    // --- Translate graph data for rendering ---

    function translateGraphData(data: ApiGraphData) {
      return {
        nodes: data.nodes.map((n: ApiGraphNode) => ({
          ...n,
          title: getTranslatedFields(n).title,
        })),
        edges: data.edges,
      };
    }

    // --- Dispose current renderer and components ---

    function disposeCurrent(): void {
      if (current) {
        current.search?.destroy();
        current.settingsPanel?.dispose();
        current.renderer?.dispose();
        document.removeEventListener('keydown', current.handleKeydown);
        current = null;
      }
      currentRenderer = null;
    }

    // --- Keyboard handler ---

    function handleKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && neighborMode) {
        exitNeighborMode();
      }
      // Ctrl+Shift+= to increase depth, Ctrl+Shift+- to decrease
      if (neighborMode && e.ctrlKey && e.shiftKey) {
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          if (neighborDepth < 5) {
            neighborDepth++;
            if (depthSlider) depthSlider.setValue(neighborDepth);
            applyNeighborFilter();
          }
        } else if (e.key === '_' || e.key === '-') {
          e.preventDefault();
          if (neighborDepth > 1) {
            neighborDepth--;
            if (depthSlider) depthSlider.setValue(neighborDepth);
            applyNeighborFilter();
          }
        }
      }
    }

    // --- Render the graph (once) ---

    function renderGraph(): void {
      disposeCurrent();

      const translatedData = translateGraphData(graphData);
      const savedSettings = getGraphSettings();
      const renderer = createGraphRenderer(graphContainer!, translatedData, {
        cameraZ: 80,
        onNodeClick: (slug: string) => {
          closeSidebar();
          if (neighborMode) {
            // Re-center on the clicked node
            neighborCenterSlug = slug;
            applyNeighborFilter();
          } else {
            enterNeighborMode(slug);
          }
          showConceptSidebar(slug);
        },
        onEdgeClick: (source: string, target: string) => {
          showCrosslinkSidebar(source, target);
        },
        graphSettings: savedSettings,
      });

      const settingsPanel = createGraphSettingsPanel(graphContainer!, renderer);
      const search = createGraphSearch(graphContainer!, translatedData.nodes, renderer);

      current = { renderer, settingsPanel, search, handleKeydown };
      currentRenderer = renderer;
      document.addEventListener('keydown', handleKeydown);

      updateDensityStats(graphData);
    }

    // --- Neighbor mode ---

    function applyNeighborFilter(): void {
      if (!current?.renderer) return;
      const subgraph = extractNeighborhood(graphData, neighborCenterSlug!, neighborDepth);
      const slugs = subgraph.nodes.map(n => n.slug);
      current.renderer.highlightNodes(slugs);
      updateDensityStats(subgraph);
    }

    function enterNeighborMode(slug: string): void {
      if (!graphData.nodes.some((n: ApiGraphNode) => n.slug === slug)) return;
      neighborMode = true;
      neighborCenterSlug = slug;

      // Show back button + depth slider
      backButton!.style.display = 'block';
      backButton!.innerHTML = `&larr; ${translate('common.back_to_network', 'Back to network')}`;
      graphContainer!.classList.add('neighbor-active');

      if (!depthSlider) {
        depthSlider = createDepthSlider(graphContainer!, {
          initial: neighborDepth,
          onChange: (newDepth: number) => {
            neighborDepth = newDepth;
            applyNeighborFilter();
          },
        });
      }

      applyNeighborFilter();
    }

    function exitNeighborMode(): void {
      neighborMode = false;
      neighborCenterSlug = null;
      closeSidebar();

      if (depthSlider) {
        depthSlider.destroy();
        depthSlider = null;
      }

      backButton!.style.display = 'none';
      graphContainer!.classList.remove('neighbor-active');

      // Restore full graph by passing null (clears highlight filter)
      if (current?.renderer) {
        current.renderer.highlightNodes(null);
      }
      updateDensityStats(graphData);
    }

    // --- Build the DOM shell (called once, or on language change) ---

    function renderContent(): void {
      disposeCurrent();
      if (depthSlider) {
        depthSlider.destroy();
        depthSlider = null;
      }
      container.innerHTML = '';

      const view = document.createElement('div');
      view.className = 'view view--graph graph-view';

      // Header
      const header = document.createElement('div');
      header.className = 'graph-header';

      if (graphData.nodes.length === 0) {
        view.innerHTML = `
          <div class="not-found" style="min-height: 40vh;">
            <p class="not-found__message">${translate('graph.empty', 'No concepts yet. The network is empty.')}</p>
          </div>
        `;
        container.appendChild(view);
        return;
      }

      // Graph container with sidebar
      const graphWrapper = document.createElement('div');
      graphWrapper.className = 'graph-graph-wrapper';

      graphContainer = document.createElement('div');
      graphContainer.className = 'graph-graph-container';

      // Graph legend
      const legend = document.createElement('div');
      legend.className = 'graph-legend';
      legend.innerHTML = `
        <span class="graph-legend__item"><span class="graph-legend__key">${translate('legend.click', 'click')}</span> ${translate('legend.node_to_explore', 'node to explore')}</span>
        <span class="graph-legend__item"><span class="graph-legend__key">${translate('legend.drag', 'drag')}</span> ${translate('legend.to_pan', 'to pan')}</span>
        <span class="graph-legend__item"><span class="graph-legend__key">${translate('legend.scroll', 'scroll')}</span> ${translate('legend.to_zoom', 'to zoom')}</span>
        <span class="graph-legend__item graph-legend__item--neighbor"><span class="graph-legend__key">Ctrl &#x21E7; +</span> ${translate('legend.more_depth', 'more depth')}</span>
        <span class="graph-legend__item graph-legend__item--neighbor"><span class="graph-legend__key">Ctrl &#x21E7; &minus;</span> ${translate('legend.less_depth', 'less depth')}</span>
        <span class="graph-legend__item graph-legend__item--neighbor"><span class="graph-legend__key">Esc</span> ${translate('legend.back_to_network', 'back to network')}</span>
      `;
      graphContainer.appendChild(legend);

      // Density stats overlay
      const densityStats = document.createElement('div');
      densityStats.className = 'graph-density-stats';
      graphContainer.appendChild(densityStats);

      // Back button (hidden by default)
      backButton = document.createElement('button');
      backButton.className = 'focus-back-btn';
      backButton.style.display = 'none';
      backButton.innerHTML = `&larr; ${translate('common.back_to_network', 'Back to network')}`;
      backButton.addEventListener('click', exitNeighborMode);
      graphContainer.appendChild(backButton);

      graphWrapper.appendChild(graphContainer);

      // Sidebar (hidden by default)
      sidebar = document.createElement('div');
      sidebar.className = 'graph-sidebar';
      sidebar.style.display = 'none';
      sidebar.innerHTML = `
        <button class="graph-sidebar__close">&times;</button>
        <div class="graph-sidebar__content">
          <div class="loading">${translate('common.loading', 'Loading...')}</div>
        </div>
      `;
      (sidebar.querySelector('.graph-sidebar__close') as HTMLButtonElement).addEventListener('click', closeSidebar);
      graphWrapper.appendChild(sidebar);

      view.appendChild(graphWrapper);
      container.appendChild(view);

      // Render the graph (always full data, neighbor mode uses highlightNodes)
      renderGraph();

      // Re-apply neighbor filter if we were in neighbor mode (e.g. after language change)
      if (neighborMode && neighborCenterSlug) {
        enterNeighborMode(neighborCenterSlug);
      }
    }

    renderContent();

    // Auto-focus from query params (e.g., /graph?focus=slug or /graph?edge=source,target)
    const params = new URLSearchParams(window.location.search);
    const edgeParam = params.get('edge');
    const focusParam = params.get('focus');
    if ((edgeParam || focusParam) && currentRenderer) {
      setTimeout(() => {
        if (edgeParam) {
          const [sourceSlug, targetSlug] = edgeParam.split(',');
          if (sourceSlug && targetSlug) {
            enterNeighborMode(sourceSlug);
            showCrosslinkSidebar(sourceSlug, targetSlug);
          }
        } else if (focusParam) {
          enterNeighborMode(focusParam);
          showConceptSidebar(focusParam);
        }
      }, 300);
      history.replaceState({}, '', '/graph');

      console.log(`Took ${Date.now() - time} ms to render`);
    }

    // Re-render when language changes (preserve neighbor mode state)
    langUnsub = subscribe('lang', () => {
      renderContent();
    });

    console.log(`Took ${Date.now() - time} ms to run code`);

    return () => {
      if (langUnsub) langUnsub();
      disposeCurrent();
      if (depthSlider) {
        depthSlider.destroy();
        depthSlider = null;
      }
    };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error">${translate('graph.error', 'Failed to load network: ')}${(err as Error).message}</p></div>`;
    return () => {
      if (langUnsub) langUnsub();
    };
  }
}
